import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Eye, Pencil } from "lucide-react";
import { getDraft, saveDraft, publishWhitepaper } from "@/api";
import { WhitepaperBody } from "@/pages/Whitepaper";

export const WhitepaperEditor = ({ token, showToast }) => {
  const [sections, setSections] = useState(null);
  const [meta, setMeta] = useState({});
  const [preview, setPreview] = useState(false);
  const [note, setNote] = useState("");
  const [major, setMajor] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getDraft(token).then((d) => { setSections(d.sections); setMeta(d); }); }, [token]);
  if (!sections) return <div className="text-slate-400">Loading draft…</div>;

  const upd = (i, k, v) => setSections((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, d) => setSections((s) => { const n = [...s]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const err = (e) => showToast(e?.response?.data?.detail || "Failed", "error");
  const save = async () => { setBusy(true); try { await saveDraft(token, sections); showToast("Draft saved"); } catch (e) { err(e); } finally { setBusy(false); } };
  const publish = async () => {
    setBusy(true);
    try { await saveDraft(token, sections); const r = await publishWhitepaper(token, note, major); showToast(`Published v${r.version}`); setNote(""); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <div data-testid="wp-editor">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-slate-400">Based on v{meta.based_on}{meta.has_draft ? " · unsaved changes are kept in your draft" : ""}</div>
        <div className="flex-1" />
        <button data-testid="wp-editor-preview-toggle" onClick={() => setPreview(!preview)} className="btn-tactile btn-ghost text-sm flex items-center gap-1">{preview ? <><Pencil size={14} /> Edit</> : <><Eye size={14} /> Preview</>}</button>
        <button data-testid="wp-editor-save" onClick={save} disabled={busy} className="btn-tactile btn-ghost text-sm">Save draft</button>
      </div>
      {preview ? <div className="mt-6 card-tile"><WhitepaperBody sections={sections} /></div> : (
        <div className="mt-4 space-y-3">
          {sections.map((s, i) => (
            <div key={s.id} className="card-tile" data-testid={`wp-editor-section-${i}`}>
              <div className="flex items-center gap-2">
                <input data-testid={`wp-editor-title-${i}`} value={s.title} onChange={(e) => upd(i, "title", e.target.value)} className="flex-1 bg-[#12131C] border border-[#2A2E45] rounded-lg px-3 py-2 font-display font-bold" />
                <button aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="btn-tactile btn-ghost p-2"><ArrowUp size={14} /></button>
                <button aria-label="Move down" disabled={i === sections.length - 1} onClick={() => move(i, 1)} className="btn-tactile btn-ghost p-2"><ArrowDown size={14} /></button>
                <button aria-label="Delete section" data-testid={`wp-editor-delete-${i}`} onClick={() => setSections((x) => x.filter((_, j) => j !== i))} className="btn-tactile btn-ghost p-2 text-rose-400"><Trash2 size={14} /></button>
              </div>
              <textarea data-testid={`wp-editor-body-${i}`} value={s.body} onChange={(e) => upd(i, "body", e.target.value)} rows={8} className="mt-2 w-full bg-[#12131C] border border-[#2A2E45] rounded-lg px-3 py-2 font-mono text-xs" />
            </div>
          ))}
          <button data-testid="wp-editor-add-section" onClick={() => setSections((s) => [...s, { id: `s${Date.now()}`, title: `${s.length + 1}. New section`, body: "" }])} className="btn-tactile btn-ghost text-sm flex items-center gap-1"><Plus size={14} /> Add section</button>
        </div>
      )}
      <div className="mt-6 card-tile card-flame">
        <div className="font-display font-bold">Publish a new version</div>
        <input data-testid="wp-publish-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Changelog note, e.g. Added referral program details" className="mt-2 w-full bg-[#12131C] border border-[#2A2E45] rounded-lg px-3 py-2 text-sm" />
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={major} onChange={(e) => setMajor(e.target.checked)} data-testid="wp-publish-major" /> Major version (e.g. 1.x → 2.0)</label>
        <button data-testid="wp-publish-button" onClick={publish} disabled={busy || !note.trim()} className="btn-tactile btn-flame text-sm mt-3">Publish</button>
      </div>
    </div>
  );
};
